import { FC } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import useTheme from '@base/useTheme';
import { TileRepresentation, TileVisualization, TileVisualizationType } from '@common/api';
import { Button } from '@components/button';
import { ThreeDots } from '@components/icons/FarseerIcons';
import { FormField, SwitchField, TextArea, TextInput } from '@components/form';
import { Divider } from '@components/layout/Divider';
import { FlexCol, FlexGrow, FlexRow } from '@components/layout/Flex';
import { Popover } from '@components/popover/Popover';
import { ColorPicker } from '@features/colors/ColorPicker';
import { useDebounceHandler } from '@hooks/useDebounceHandler';
import { useDerivedState } from '@hooks/useDerivedState';
import { Update } from '@use-gpu/state';
import { TileDataType } from '@util/enums';

export type TileMetaEditProps = {
    tile: TileRepresentation;
    canEdit?: boolean;
    onChange: (update: Update<TileRepresentation>) => void;
};

export const TileMetaEdit: FC<TileMetaEditProps> = (props: TileMetaEditProps) => {
    const { tile, canEdit, onChange } = props;

    const { LL } = useI18nContext();
    const { colors } = useTheme();

    const tileVisualization: TileVisualization | null =
        tile.data.type === TileDataType.SHEET_BACKED ? tile.data.visualization : null;

    const savedName = tile.name || '';
    const [name, setName] = useDerivedState(savedName, [savedName]);
    useDebounceHandler(
        () => {
            if (tile.name !== name) {
                onChange({ name });
            }
        },
        name,
        500
    );

    const savedSubtitle = tile?.subtitle || '';
    const [subtitle, setSubtitle] = useDerivedState(savedSubtitle, [savedSubtitle]);
    useDebounceHandler(
        () => {
            if (tile.subtitle !== subtitle) {
                onChange({ subtitle });
            }
        },
        subtitle,
        500
    );

    const savedFootnotes = tile?.footnotes || '';
    const [footnotes, setFootnotes] = useDerivedState(savedFootnotes, [savedFootnotes]);
    useDebounceHandler(
        () => {
            if (tile.footnotes !== footnotes) {
                onChange({ footnotes });
            }
        },
        footnotes,
        500
    );

    const isTextTile = tile.data.type === 'TEXT';
    const isSheetTile = tileVisualization?.type === TileVisualizationType.Sheet;

    const hasNotes = !isTextTile && !isSheetTile;

    const notesNode = hasNotes ? (
        <FlexCol>
            <SwitchField
                label={LL.SUBTITLE()}
                value={tile.style.showSubtitle}
                onChange={(showSubtitle) => onChange({ style: { showSubtitle } })}
            />
            {tile.style.showSubtitle ? (
                <div style={{ position: 'relative', top: -6 }}>
                    <TextArea autoSize rows={1} maxRows={4} value={subtitle} onChange={setSubtitle} />
                </div>
            ) : null}

            <SwitchField
                label={LL.FOOTNOTES()}
                value={tile.style.showFootnotes}
                onChange={(showFootnotes) => onChange({ style: { showFootnotes } })}
            />
            {tile.style.showFootnotes ? (
                <div style={{ position: 'relative', top: -6 }}>
                    <TextArea autoSize rows={1} maxRows={4} value={footnotes} onChange={setFootnotes} />
                </div>
            ) : null}
            <Divider />
        </FlexCol>
    ) : null;

    const styleNode = (
        <FlexCol>
            <SwitchField
                label={LL.BACKGROUND()}
                value={tile.style.showBackground}
                onChange={(showBackground) => onChange({ style: { showBackground } })}
                suffix={
                    <ColorPicker
                        value={tile.style.backgroundColor ?? colors.ui.white}
                        onChange={(backgroundColor) => {
                            onChange({ style: { showBackground: true, backgroundColor } });
                        }}
                    />
                }
            />

            <SwitchField
                label={LL.BORDER()}
                value={tile.style.showBorder}
                onChange={(showBorder) => onChange({ style: { showBorder } })}
                suffix={
                    <ColorPicker
                        value={tile.style.borderColor ?? colors.neutral.w200}
                        onChange={(borderColor) => onChange({ style: { showBorder: true, borderColor } })}
                    />
                }
            />

            <SwitchField
                label={LL.SHADOW()}
                value={tile.style.hasShadow}
                onChange={(hasShadow) => onChange({ style: { hasShadow } })}
            />
        </FlexCol>
    );

    const popoverNode = canEdit ? (
        <Popover placement="bottom-start" mainOffset={4}>
            <Popover.Trigger>
                <Button $type="quaternary" icon={ThreeDots} />
            </Popover.Trigger>
            <Popover.Content width={345} noFocus $padding="7px">
                <FlexCol
                    // Extra padding for selection outline spill
                    style={{ padding: 1 }}
                >
                    {notesNode}
                    {styleNode}
                </FlexCol>
            </Popover.Content>
        </Popover>
    ) : null;

    const viewNode = (
        <FormField label={LL.NAME_AND_STYLE()}>
            <FlexRow $gap={8}>
                <FlexGrow>
                    <TextInput
                        disabled={!canEdit}
                        name="name"
                        value={name}
                        onChange={setName}
                        placeholder={LL.ENTER_TILE_NAME()}
                    />
                </FlexGrow>
                {popoverNode}
            </FlexRow>
        </FormField>
    );

    return viewNode;
};
